import type { BaseUsecase } from '@usecases/base-usecase';
import type { Agent, UserInfo } from '@entities/user';
import { Contact, Constants } from '@salesforce/scv-connector-base';

export interface ShowAgentListRepository {
  getAgents(): Promise<Agent[]>;
}

export interface ShowAgentListEventPublisher {
  publish(): Promise<void>;
}

export interface AgentUserRepository {
  getUserInfo(): UserInfo | undefined;
}

export interface ShowAgentListUsecase extends BaseUsecase<Contact[] | void> {
  run(): Promise<Contact[] | void>;
}

export class ShowAgentList implements ShowAgentListUsecase {
  constructor(
    private userRepository: AgentUserRepository,
    private repository: ShowAgentListRepository,
    private publisher: ShowAgentListEventPublisher,
  ) {}

  async run(): Promise<Contact[] | void> {
    const agents = await this.repository.getAgents();
    const currentUser = this.userRepository.getUserInfo() as UserInfo;
    const uniqueAgentIDs = new Set<string>();
    const contacts = agents
      .filter((agent) => {
        const isEligible =
          agent.ID !== String(currentUser.id) &&
          agent.availabilityStatus &&
          ['available', 'on_mobile'].includes(
            agent.availabilityStatus.toLowerCase(),
          ) &&
          !uniqueAgentIDs.has(agent.ID);
        if (isEligible) {
          uniqueAgentIDs.add(agent.ID);
        }
        return isEligible;
      })
      .map(
        (agent) =>
          new Contact({
            id: agent.ID,
            type: Constants.CONTACT_TYPE.AGENT,
            name: agent.fullName,
            availability: 'AVAILABLE',
          }),
      );

    await this.publisher.publish();
    return contacts;
  }
}
